import { useState, lazy, Suspense, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useDataLayer } from '@/hooks/useDataLayer';
import { useBooking } from '@/contexts/BookingContext';

// Critical components - loaded immediately
import Header from '@/components/sections/Header';
import HeroSection from '@/components/sections/HeroSection';

// Lazy loaded components - below the fold
const StatsSection = lazy(() => import('@/components/sections/StatsSection'));
const SpecialtySection = lazy(() => import('@/components/sections/SpecialtySection'));
const ProceduresSection = lazy(() => import('@/components/sections/ProceduresSection'));
const AboutSection = lazy(() => import('@/components/sections/AboutSection'));
const ClinicSection = lazy(() => import('@/components/sections/ClinicSection'));
const JourneySection = lazy(() => import('@/components/sections/JourneySection'));
const TestimonialsSection = lazy(() => import('@/components/sections/TestimonialsSection'));
const StructureSection = lazy(() => import('@/components/sections/StructureSection'));
const VideoSection = lazy(() => import('@/components/sections/VideoSection'));
const BlogSection = lazy(() => import('@/components/sections/BlogSection'));
const LocationSection = lazy(() => import('@/components/sections/LocationSection'));
const Footer = lazy(() => import('@/components/sections/Footer'));

// SEO da home. Ficava fixo no index.html e por isso era herdado por todas as
// rotas (canonical/description/og duplicados). Agora vive aqui, como nas demais
// páginas — o index.html só carrega o que é global (Physician, MedicalBusiness,
// WebSite).
const HOME_TITLE = 'Dr. Luis Brunello | Cirurgia Plástica em Curitiba';
const HOME_DESCRIPTION =
  'Dr. Luis Brunello, cirurgião plástico em Curitiba. Especialista em cirurgias das mamas, contorno corporal e pós-emagrecimento. CRM-PR 40.350.';

const HOME_WEBPAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://luisbrunello.com.br/#webpage',
  url: 'https://luisbrunello.com.br/',
  name: HOME_TITLE,
  description: HOME_DESCRIPTION,
  isPartOf: { '@id': 'https://luisbrunello.com.br/#website' },
  about: { '@id': 'https://luisbrunello.com.br/#physician' },
  inLanguage: 'pt-BR',
};

const HOME_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Quais procedimentos o Dr. Luis Brunello realiza?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O Dr. Luis Brunello realiza cirurgias das mamas (aumento, redução e correção), contorno corporal (lipoaspiração, abdominoplastia, miniabdominoplastia), remodelamento pós-emagrecimento, pós-gestação e mommy makeover.',
      },
    },
    {
      '@type': 'Question',
      name: 'Onde o Dr. Luis Brunello atende?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O Dr. Luis Brunello atende em Curitiba – PR. CRM-PR 40.350 | CRM-SP 241.031. O atendimento é realizado mediante agendamento prévio.',
      },
    },
    {
      '@type': 'Question',
      name: 'O Dr. Luis Brunello é especialista em cirurgia de mama?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sim. O Dr. Luis Brunello é cirurgião plástico formado pela UNESP com atuação focada em cirurgias das mamas, contorno corporal e procedimentos pós-emagrecimento, áreas nas quais desenvolveu experiência durante sua formação e prática hospitalar.',
      },
    },
  ],
};

// Loading fallback component - height approximates section size to prevent CLS
const SectionLoader = () => (
  <div className="min-h-[500px] flex items-center justify-center bg-dark-base">
    <div className="w-8 h-8 border-2 border-light-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const Index = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dataLayer = useDataLayer();
  const { handleWhatsAppOpen } = useBooking();
  const { hash } = useLocation();

  // Trata navegação via hash (ex: vindo de outra página com /#depoimentos).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');

    // Tenta imediato primeiro (seção já renderizada)
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }

    // Aguarda o módulo da seção terminar de carregar.
    let attempts = 0;
    const poll = setInterval(() => {
      const target = document.getElementById(id);
      if (target) {
        clearInterval(poll);
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (++attempts >= 30) {
        clearInterval(poll);
      }
    }, 50);
    return () => clearInterval(poll);
  }, [hash]);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: sectionId === 'agendamento-form' ? 'center' : 'start' });
      dataLayer.trackNavigationClick(sectionId);
      setTimeout(() => { dataLayer.trackScrollToSection(sectionId); }, 1000);
    }
    setIsMenuOpen(false);
  }, [dataLayer]);

  return (
    <div className="min-h-screen bg-dark-base">
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <link rel="canonical" href="https://luisbrunello.com.br/" />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
        <meta property="og:url" content="https://luisbrunello.com.br/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Dr. Luis Brunello" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:image" content="https://luisbrunello.com.br/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Dr. Luis Brunello – Cirurgia Plástica em Curitiba" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta name="twitter:description" content={HOME_DESCRIPTION} />
        <meta name="twitter:image" content="https://luisbrunello.com.br/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(HOME_WEBPAGE_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(HOME_FAQ_SCHEMA)}</script>
      </Helmet>

      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={scrollToSection}
        onOpenBooking={handleWhatsAppOpen}
      />

      <main>
        <HeroSection scrollToSection={scrollToSection} onOpenBooking={handleWhatsAppOpen} />

        {/* StatsSection: first below-fold, load eagerly */}
        <Suspense fallback={<SectionLoader />}>
          <StatsSection />
        </Suspense>

        {/* Carrega o conteúdo sem depender de rolagem, inclusive no pré-render. */}
        <Suspense fallback={<SectionLoader />}>
          <SpecialtySection onOpenBooking={handleWhatsAppOpen} />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <ProceduresSection onOpenBooking={handleWhatsAppOpen} />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <AboutSection onOpenBooking={handleWhatsAppOpen} />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <ClinicSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <JourneySection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <TestimonialsSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <StructureSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <VideoSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <BlogSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <LocationSection onOpenBooking={handleWhatsAppOpen} />
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
